/**
 * Stateful Tactics scene with multi-frame support, undo/redo and
 * dirty-tracking. All mutating actions operate on the *current* frame; the
 * caller only sees `state.scene` (the current frame's scene) and a few frame
 * navigation helpers.
 *
 * The reducer keeps a 50-entry history of `BoardSnapshot`s. Any mutating
 * action pushes the previous snapshot onto `past` and clears `future`.
 * `undo`/`redo` swap between the two stacks.
 *
 * Phase 4 additions:
 *  - `currentFrameIndex` selects which frame the API reads from / writes to.
 *  - Frame helpers: `addFrame`, `duplicateFrame`, `removeFrame`,
 *    `selectFrame`, `setFrameDuration`, `reorderFrame`.
 *
 * Recording mode ("Aufnahme"): while `recording` is on, every committed edit
 * whose type is listed in `RECORDED_ACTIONS` is routed through `recordEdit`,
 * which inserts a new frame after the current one, applies the edit to that
 * new frame and selects it — all as ONE history entry, so a single undo
 * removes both the frame and the change.
 */
import { useCallback, useMemo, useReducer, useRef } from "react";

import {
  type BoardSnapshot,
  boardDimensions,
  clampToPitch,
  DEFAULT_FRAME_DURATION_MS,
  emptyScene,
  type Frame,
  type Orientation,
  rotateSceneForFlip,
  type Scene,
  type Shape,
  type Stroke,
  scaleSceneToBoard,
  type TextAnnotation,
  type Token,
} from "./scene.js";

const HISTORY_LIMIT = 50;

interface InternalState {
  snapshot: BoardSnapshot;
  initial: BoardSnapshot;
  past: BoardSnapshot[];
  future: BoardSnapshot[];
  currentFrameIndex: number;
  /**
   * Snapshot captured the moment a transient gesture (e.g. live drag/resize)
   * starts mutating the scene without pushing to history. The next committed
   * mutating action will push this snapshot onto `past` instead of the
   * current (already live-updated) snapshot, so undo reverts the whole
   * gesture in one step.
   */
  transientBase: BoardSnapshot | null;
  /** Recording mode — committed edits branch into a new frame. */
  recording: boolean;
}

type Action =
  | { type: "replace"; snapshot: BoardSnapshot }
  | { type: "markClean" }
  | { type: "undo" }
  | { type: "redo" }
  | {
      type: "setMeta";
      patch: Partial<
        Pick<BoardSnapshot, "fieldKind" | "orientation" | "tokenSizePreset" | "nameDisplay">
      >;
    }
  | { type: "selectFrame"; index: number }
  | { type: "addFrame"; durationMs?: number }
  | { type: "duplicateFrame"; sourceIndex?: number }
  | { type: "removeFrame"; index: number }
  | { type: "reorderFrame"; from: number; to: number }
  | { type: "setFrameDuration"; index: number; durationMs: number }
  | { type: "addToken"; token: Token }
  | { type: "moveToken"; id: string; x: number; y: number }
  | { type: "moveTokens"; deltas: Map<string, { x: number; y: number }> }
  | { type: "updateToken"; id: string; patch: Partial<Token> }
  | { type: "updateTokensLive"; patches: Map<string, Partial<Token>> }
  | { type: "updateTokens"; patches: Map<string, Partial<Token>> }
  | { type: "removeToken"; id: string }
  | { type: "removeTokens"; ids: Iterable<string> }
  | { type: "addStroke"; stroke: Stroke }
  | { type: "updateStroke"; id: string; patch: Partial<Stroke> }
  | { type: "removeStroke"; id: string }
  | { type: "addShape"; shape: Shape }
  | { type: "updateShape"; id: string; patch: Partial<Shape> }
  | { type: "updateShapeLive"; id: string; patch: Partial<Shape> }
  | { type: "removeShape"; id: string }
  | { type: "addText"; text: TextAnnotation }
  | { type: "updateText"; id: string; patch: Partial<TextAnnotation> }
  | { type: "removeText"; id: string }
  | { type: "setRecording"; on: boolean };

/**
 * Committed edits that become their own frame while recording. Removals,
 * label edits, meta and frame operations are deliberately left out (a bulk
 * delete dispatches one action per element and would otherwise spawn a frame
 * each). Narrow this set to restrict what recording captures.
 */
const RECORDED_ACTIONS: ReadonlySet<Action["type"]> = new Set<Action["type"]>([
  "addToken",
  "moveToken",
  "moveTokens",
  "updateTokens",
  "addStroke",
  "addShape",
  "updateShape",
  "addText",
  "updateText",
]);

/** Replace frame at index by applying `mut` to its scene. */
function withFrame(
  snap: BoardSnapshot,
  index: number,
  mut: (scene: Scene) => Scene,
): BoardSnapshot {
  const frames = snap.frames.map((f, i) => (i === index ? { ...f, scene: mut(f.scene) } : f));
  return { ...snap, frames };
}

function clampIndex(snap: BoardSnapshot, idx: number): number {
  const max = Math.max(0, snap.frames.length - 1);
  return Math.min(Math.max(idx, 0), max);
}

function applyAction(
  state: InternalState,
  action: Action,
): { snapshot: BoardSnapshot; currentFrameIndex: number; mutated: boolean } {
  const idx = state.currentFrameIndex;
  const snap = state.snapshot;
  switch (action.type) {
    case "setMeta": {
      const next: BoardSnapshot = { ...snap, ...action.patch };
      // Two meta changes move the board rectangle under the scene, and each
      // needs the coordinates remapped so the arrangement stays put relative
      // to the pitch:
      //
      //   • orientation flip — width/height swap, so the scene is ROTATED 90°
      //     (not transposed; see `rotateSceneForFlip` for why handedness
      //     matters). Uses the PRE-flip board dims as its source.
      //   • fieldKind change — a half pitch has different dimensions than a
      //     full one, so the scene is RESCALED per axis.
      //
      // When both happen at once the order matters: rotate first, in the old
      // field's coordinate space, then rescale the rotated result onto the new
      // field's rectangle.
      const nextOrientation: Orientation = next.orientation;
      const flipped = nextOrientation !== snap.orientation;
      const kindChanged = next.fieldKind !== snap.fieldKind;
      if (flipped || kindChanged) {
        let frames = snap.frames;
        if (flipped) {
          const oldDims = boardDimensions(snap.orientation, snap.fieldKind);
          frames = frames.map((f) => ({
            ...f,
            scene: rotateSceneForFlip(f.scene, nextOrientation, oldDims),
          }));
        }
        if (kindChanged) {
          const from = boardDimensions(nextOrientation, snap.fieldKind);
          const to = boardDimensions(nextOrientation, next.fieldKind);
          frames = frames.map((f) => ({
            ...f,
            scene: scaleSceneToBoard(f.scene, from, to),
          }));
        }
        next.frames = frames;
      }
      return {
        snapshot: next,
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "selectFrame": {
      const next = clampIndex(snap, action.index);
      return {
        snapshot: snap,
        currentFrameIndex: next,
        mutated: false,
      };
    }
    case "addFrame": {
      const newFrame: Frame = {
        id: crypto.randomUUID(),
        durationMs: action.durationMs ?? DEFAULT_FRAME_DURATION_MS,
        // Clone the current frame so the new keyframe starts where we left off
        scene: cloneScene(snap.frames[idx]?.scene ?? emptyScene()),
      };
      const frames = [...snap.frames];
      frames.splice(idx + 1, 0, newFrame);
      return {
        snapshot: { ...snap, frames },
        currentFrameIndex: idx + 1,
        mutated: true,
      };
    }
    case "duplicateFrame": {
      const src = action.sourceIndex ?? idx;
      const source = snap.frames[src];
      if (!source) return { snapshot: snap, currentFrameIndex: idx, mutated: false };
      const newFrame: Frame = {
        id: crypto.randomUUID(),
        durationMs: source.durationMs,
        scene: cloneScene(source.scene),
      };
      const frames = [...snap.frames];
      frames.splice(src + 1, 0, newFrame);
      return {
        snapshot: { ...snap, frames },
        currentFrameIndex: src + 1,
        mutated: true,
      };
    }
    case "removeFrame": {
      if (snap.frames.length <= 1) {
        return { snapshot: snap, currentFrameIndex: idx, mutated: false };
      }
      const frames = snap.frames.filter((_, i) => i !== action.index);
      const next = Math.min(idx, frames.length - 1);
      return {
        snapshot: { ...snap, frames },
        currentFrameIndex: next,
        mutated: true,
      };
    }
    case "reorderFrame": {
      const { from, to } = action;
      if (from === to || from < 0 || from >= snap.frames.length) {
        return { snapshot: snap, currentFrameIndex: idx, mutated: false };
      }
      const frames = [...snap.frames];
      const [f] = frames.splice(from, 1);
      if (!f) return { snapshot: snap, currentFrameIndex: idx, mutated: false };
      frames.splice(Math.min(to, frames.length), 0, f);
      let nextIdx = idx;
      if (idx === from) nextIdx = to;
      else if (from < idx && to >= idx) nextIdx = idx - 1;
      else if (from > idx && to <= idx) nextIdx = idx + 1;
      return {
        snapshot: { ...snap, frames },
        currentFrameIndex: clampIndex({ ...snap, frames }, nextIdx),
        mutated: true,
      };
    }
    case "setFrameDuration": {
      const frames = snap.frames.map((f, i) =>
        i === action.index ? { ...f, durationMs: Math.max(100, action.durationMs) } : f,
      );
      return {
        snapshot: { ...snap, frames },
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "addToken": {
      const orientation = snap.orientation;
      const clamped = clampToPitch(action.token.x, action.token.y, orientation);
      const token = { ...action.token, ...clamped };
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: [...s.tokens, token],
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "moveToken": {
      const orientation = snap.orientation;
      const c = clampToPitch(action.x, action.y, orientation);
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: s.tokens.map((t) => (t.id === action.id ? { ...t, x: c.x, y: c.y } : t)),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "moveTokens": {
      const orientation = snap.orientation;
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: s.tokens.map((t) => {
            const d = action.deltas.get(t.id);
            if (!d) return t;
            const c = clampToPitch(d.x, d.y, orientation);
            return { ...t, x: c.x, y: c.y };
          }),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "updateToken": {
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: s.tokens.map((t) => (t.id === action.id ? { ...t, ...action.patch } : t)),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "updateTokens": {
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: s.tokens.map((t) => {
            const p = action.patches.get(t.id);
            return p ? { ...t, ...p } : t;
          }),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "removeToken": {
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: s.tokens.filter((t) => t.id !== action.id),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "removeTokens": {
      const set = new Set(action.ids);
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          tokens: s.tokens.filter((t) => !set.has(t.id)),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    }
    case "addStroke":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          strokes: [...s.strokes, action.stroke],
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "updateStroke":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          strokes: s.strokes.map((st) => (st.id === action.id ? { ...st, ...action.patch } : st)),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "removeStroke":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          strokes: s.strokes.filter((st) => st.id !== action.id),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "addShape":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          shapes: [...s.shapes, action.shape],
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "updateShape":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          shapes: s.shapes.map((sh) => (sh.id === action.id ? { ...sh, ...action.patch } : sh)),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "removeShape":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          shapes: s.shapes.filter((sh) => sh.id !== action.id),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "addText":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          texts: [...s.texts, action.text],
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "updateText":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          texts: s.texts.map((tx) => (tx.id === action.id ? { ...tx, ...action.patch } : tx)),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "removeText":
      return {
        snapshot: withFrame(snap, idx, (s) => ({
          ...s,
          texts: s.texts.filter((tx) => tx.id !== action.id),
        })),
        currentFrameIndex: idx,
        mutated: true,
      };
    case "replace":
    case "markClean":
    case "undo":
    case "redo":
    case "updateShapeLive":
    case "updateTokensLive":
    case "setRecording":
      return { snapshot: snap, currentFrameIndex: idx, mutated: false };
  }
}

function cloneScene(s: Scene): Scene {
  return {
    tokens: s.tokens.map((t) => ({ ...t })),
    strokes: s.strokes.map((st) => ({ ...st, points: [...st.points] })),
    shapes: s.shapes.map((sh) => ({ ...sh })),
    texts: s.texts.map((tx) => ({ ...tx })),
  };
}

function reducer(state: InternalState, action: Action): InternalState {
  if (action.type === "replace") {
    return {
      snapshot: action.snapshot,
      initial: action.snapshot,
      past: [],
      future: [],
      currentFrameIndex: clampIndex(action.snapshot, state.currentFrameIndex),
      transientBase: null,
      // A background re-feed (e.g. after saving) must not end a recording.
      recording: state.recording,
    };
  }
  if (action.type === "markClean") {
    // After a successful save we mark the current snapshot as the new
    // pristine baseline so the dirty pill clears immediately, regardless of
    // whether the parent later re-feeds a reparsed (new-reference) snapshot.
    if (state.snapshot === state.initial) return state;
    return { ...state, initial: state.snapshot };
  }
  if (action.type === "undo") {
    const prev = state.past[state.past.length - 1];
    if (!prev) return state;
    return {
      ...state,
      snapshot: prev,
      past: state.past.slice(0, -1),
      future: [state.snapshot, ...state.future],
      currentFrameIndex: frameIndexAfterSwap(state.snapshot, prev, state.currentFrameIndex),
      transientBase: null,
    };
  }
  if (action.type === "redo") {
    const next = state.future[0];
    if (!next) return state;
    return {
      ...state,
      snapshot: next,
      past: [...state.past, state.snapshot].slice(-HISTORY_LIMIT),
      future: state.future.slice(1),
      currentFrameIndex: frameIndexAfterSwap(state.snapshot, next, state.currentFrameIndex),
      transientBase: null,
    };
  }
  if (action.type === "setRecording") {
    return state.recording === action.on ? state : { ...state, recording: action.on };
  }
  if (state.recording && RECORDED_ACTIONS.has(action.type)) {
    return recordEdit(state, action);
  }
  if (action.type === "updateShapeLive") {
    // Transient update during an in-flight drag/resize. Mutates the current
    // snapshot in place (so the React tree re-renders with the new geometry
    // and the resize handles follow live) but skips pushing to `past` —
    // a single drag would otherwise flood the 50-entry history. We remember
    // the snapshot from *before* the gesture started in `transientBase`;
    // the final commit on dragEnd uses the regular `updateShape` action,
    // which will push `transientBase` (not the live-mutated snapshot) onto
    // history so undo reverts the entire gesture in one step.
    const { id, patch } = action;
    const idx = state.currentFrameIndex;
    return {
      ...state,
      snapshot: withFrame(state.snapshot, idx, (s) => ({
        ...s,
        shapes: s.shapes.map((sh) => (sh.id === id ? { ...sh, ...patch } : sh)),
      })),
      transientBase: state.transientBase ?? state.snapshot,
    };
  }
  if (action.type === "updateTokensLive") {
    // Mirror of `updateShapeLive` for token resize gestures. Lets the
    // Transformer handles follow the visual scale of multi-selected
    // tokens while keeping a single undo step for the whole drag.
    const { patches } = action;
    const idx = state.currentFrameIndex;
    return {
      ...state,
      snapshot: withFrame(state.snapshot, idx, (s) => ({
        ...s,
        tokens: s.tokens.map((t) => {
          const p = patches.get(t.id);
          return p ? { ...t, ...p } : t;
        }),
      })),
      transientBase: state.transientBase ?? state.snapshot,
    };
  }
  const { snapshot, currentFrameIndex, mutated } = applyAction(state, action);
  if (!mutated && snapshot === state.snapshot) {
    return currentFrameIndex === state.currentFrameIndex ? state : { ...state, currentFrameIndex };
  }
  if (!mutated) {
    return { ...state, snapshot, currentFrameIndex };
  }
  const historyBase = state.transientBase ?? state.snapshot;
  return {
    ...state,
    snapshot,
    currentFrameIndex,
    past: [...state.past, historyBase].slice(-HISTORY_LIMIT),
    future: [],
    transientBase: null,
  };
}

/**
 * Recording: branch a new frame off the pre-gesture state (so live
 * drag/resize updates stay out of the previous frame), apply the edit to that
 * new frame, select it and push a single history entry for both.
 */
function recordEdit(state: InternalState, action: Action): InternalState {
  const base = state.transientBase ?? state.snapshot;
  const added = applyAction(
    { ...state, snapshot: base },
    { type: "addFrame", durationMs: DEFAULT_FRAME_DURATION_MS },
  );
  const applied = applyAction(
    { ...state, snapshot: added.snapshot, currentFrameIndex: added.currentFrameIndex },
    action,
  );
  if (!applied.mutated) return state;
  return {
    ...state,
    snapshot: applied.snapshot,
    currentFrameIndex: applied.currentFrameIndex,
    past: [...state.past, base].slice(-HISTORY_LIMIT),
    future: [],
    transientBase: null,
  };
}

/**
 * Pick the frame to show after undo/redo swaps `from` for `to`: jump to a
 * frame that only exists in `to` (redo of an added/recorded frame), keep the
 * current frame if it survives, otherwise fall back to its predecessor (undo
 * of an added/recorded frame lands on the frame it was branched from).
 */
function frameIndexAfterSwap(from: BoardSnapshot, to: BoardSnapshot, idx: number): number {
  const fromIds = new Set(from.frames.map((f) => f.id));
  const added = to.frames.findIndex((f) => !fromIds.has(f.id));
  if (added >= 0) return added;
  const currentId = from.frames[idx]?.id;
  const kept = to.frames.findIndex((f) => f.id === currentId);
  if (kept >= 0) return kept;
  return clampIndex(to, idx - 1);
}

function init(initial: BoardSnapshot): InternalState {
  return {
    snapshot: initial,
    initial,
    past: [],
    future: [],
    transientBase: null,
    currentFrameIndex: 0,
    recording: false,
  };
}

export interface UseSceneState {
  snapshot: BoardSnapshot;
  /** The Scene of the currently-selected frame. */
  scene: Scene;
  frames: Frame[];
  currentFrameIndex: number;
  dirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  /** Recording mode: committed edits each become a new frame. */
  recording: boolean;
  setRecording: (on: boolean) => void;
  // history
  undo: () => void;
  redo: () => void;
  // meta
  replace: (snap: BoardSnapshot) => void;
  /** Mark the current snapshot as the new pristine baseline (clears `dirty`). */
  markClean: () => void;
  setMeta: (
    patch: Partial<
      Pick<BoardSnapshot, "fieldKind" | "orientation" | "tokenSizePreset" | "nameDisplay">
    >,
  ) => void;
  setOrientation: (o: Orientation) => void;
  // frames
  selectFrame: (index: number) => void;
  addFrame: (durationMs?: number) => void;
  duplicateFrame: (sourceIndex?: number) => void;
  removeFrame: (index: number) => void;
  reorderFrame: (from: number, to: number) => void;
  setFrameDuration: (index: number, durationMs: number) => void;
  // tokens
  addToken: (t: Token) => void;
  moveToken: (id: string, x: number, y: number) => void;
  moveTokens: (deltas: Map<string, { x: number; y: number }>) => void;
  updateToken: (id: string, patch: Partial<Token>) => void;
  /**
   * Batch-commit a patch per token in one undo step. Used by the Transformer
   * to apply the final scale + position of every selected token after a
   * resize gesture ends.
   */
  updateTokens: (patches: Map<string, Partial<Token>>) => void;
  /**
   * Transient batch update during an in-flight resize. Skips undo history;
   * the final `updateTokens` commit will push the pre-gesture snapshot.
   */
  updateTokensLive: (patches: Map<string, Partial<Token>>) => void;
  removeToken: (id: string) => void;
  removeTokens: (ids: Iterable<string>) => void;
  hasPlayerToken: (playerId: string) => boolean;
  // strokes
  addStroke: (s: Stroke) => void;
  updateStroke: (id: string, patch: Partial<Stroke>) => void;
  removeStroke: (id: string) => void;
  // shapes
  addShape: (s: Shape) => void;
  updateShape: (id: string, patch: Partial<Shape>) => void;
  /**
   * Transient shape patch that does NOT push to undo history. Use during
   * an in-flight drag/resize to keep the rendered geometry (and handles)
   * in sync with the pointer. Commit the final value with `updateShape`
   * on dragEnd so undo treats the whole gesture as one step.
   */
  updateShapeLive: (id: string, patch: Partial<Shape>) => void;
  removeShape: (id: string) => void;
  // texts
  addText: (t: TextAnnotation) => void;
  updateText: (id: string, patch: Partial<TextAnnotation>) => void;
  removeText: (id: string) => void;
}

export function useSceneState(initialSnapshot: BoardSnapshot): UseSceneState {
  const [state, dispatch] = useReducer(reducer, initialSnapshot, init);
  const initialRef = useRef(initialSnapshot);
  if (initialRef.current !== initialSnapshot) {
    initialRef.current = initialSnapshot;
    // Never silently overwrite unsaved local edits when a periodic background
    // pull hands us a fresh snapshot reference. The route already memoises by
    // `snapshot_json` so this branch should normally only fire when the user
    // actually navigates to a different board (no dirty state) or after a
    // local save (also clean). Belt-and-braces.
    const dirty = state.snapshot !== state.initial;
    if (!dirty) {
      dispatch({ type: "replace", snapshot: initialSnapshot });
    }
  }

  const scene = useMemo(() => {
    const f = state.snapshot.frames[state.currentFrameIndex];
    return f?.scene ?? emptyScene();
  }, [state.snapshot, state.currentFrameIndex]);

  const hasPlayerToken = useCallback(
    (playerId: string) => scene.tokens.some((t) => t.kind === "player" && t.playerId === playerId),
    [scene],
  );

  return {
    snapshot: state.snapshot,
    scene,
    frames: state.snapshot.frames,
    currentFrameIndex: state.currentFrameIndex,
    dirty: state.snapshot !== state.initial,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    recording: state.recording,
    setRecording: useCallback((on) => dispatch({ type: "setRecording", on }), []),
    undo: useCallback(() => dispatch({ type: "undo" }), []),
    redo: useCallback(() => dispatch({ type: "redo" }), []),
    replace: useCallback(
      (snap: BoardSnapshot) => dispatch({ type: "replace", snapshot: snap }),
      [],
    ),
    markClean: useCallback(() => dispatch({ type: "markClean" }), []),
    setMeta: useCallback((patch) => dispatch({ type: "setMeta", patch }), []),
    setOrientation: useCallback(
      (o) => dispatch({ type: "setMeta", patch: { orientation: o } }),
      [],
    ),
    selectFrame: useCallback((index) => dispatch({ type: "selectFrame", index }), []),
    addFrame: useCallback((durationMs) => dispatch({ type: "addFrame", durationMs }), []),
    duplicateFrame: useCallback(
      (sourceIndex) => dispatch({ type: "duplicateFrame", sourceIndex }),
      [],
    ),
    removeFrame: useCallback((index) => dispatch({ type: "removeFrame", index }), []),
    reorderFrame: useCallback((from, to) => dispatch({ type: "reorderFrame", from, to }), []),
    setFrameDuration: useCallback(
      (index, durationMs) => dispatch({ type: "setFrameDuration", index, durationMs }),
      [],
    ),
    addToken: useCallback((t) => dispatch({ type: "addToken", token: t }), []),
    moveToken: useCallback((id, x, y) => dispatch({ type: "moveToken", id, x, y }), []),
    moveTokens: useCallback((deltas) => dispatch({ type: "moveTokens", deltas }), []),
    updateToken: useCallback((id, patch) => dispatch({ type: "updateToken", id, patch }), []),
    updateTokens: useCallback((patches) => dispatch({ type: "updateTokens", patches }), []),
    updateTokensLive: useCallback((patches) => dispatch({ type: "updateTokensLive", patches }), []),
    removeToken: useCallback((id) => dispatch({ type: "removeToken", id }), []),
    removeTokens: useCallback((ids) => dispatch({ type: "removeTokens", ids }), []),
    hasPlayerToken,
    addStroke: useCallback((s) => dispatch({ type: "addStroke", stroke: s }), []),
    updateStroke: useCallback((id, patch) => dispatch({ type: "updateStroke", id, patch }), []),
    removeStroke: useCallback((id) => dispatch({ type: "removeStroke", id }), []),
    addShape: useCallback((s) => dispatch({ type: "addShape", shape: s }), []),
    updateShape: useCallback((id, patch) => dispatch({ type: "updateShape", id, patch }), []),
    updateShapeLive: useCallback(
      (id, patch) => dispatch({ type: "updateShapeLive", id, patch }),
      [],
    ),
    removeShape: useCallback((id) => dispatch({ type: "removeShape", id }), []),
    addText: useCallback((t) => dispatch({ type: "addText", text: t }), []),
    updateText: useCallback((id, patch) => dispatch({ type: "updateText", id, patch }), []),
    removeText: useCallback((id) => dispatch({ type: "removeText", id }), []),
  };
}
